import type { SvgInfo } from '@wix/thunderbolt-commons/dist';
import {
  getFromColorMap,
  isGreyscale,
} from '@wix/thunderbolt-commons/dist/color';
import Color from 'color';
import type { DisplayModeProperty } from '@wix/editor-elements-definitions';
import type { SvgImageStyleType } from '../SvgImage.types';
import { SVG_TYPES } from '../constants';

export type SvgImageTransformedProps = {
  altText?: string;
  svgInfo: SvgInfo;
  displayMode: DisplayModeProperty;
  colorsMap?: Array<string>;
  shapeStyle?: SvgImageStyleType;
  svgId: string;
};

const viewBoxMatcher = /(viewBox=")([^"]*)(")/i;
const preserveAspectRatioMatcher = /(preserveAspectRatio=")([^"]*)(")/i;
const svgTagMatcher = /(<svg[^>]*)(>)/;
const fillAttributeMatcher = /fill="(.*?)"/gi;

type ComposeCallable<T> = (arg: T) => T;
const compose =
  <T>(...fns: Array<false | ComposeCallable<T>>) =>
  (arg: T): T =>
    fns.reduce((acc, fn) => {
      if (fn === false) {
        return acc;
      }
      return fn(acc);
    }, arg);

// this is required cause later we are using dangerouslySetInnerHTML
function escapeHtml(str: string) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/>/g, '&gt;')
    .replace(/</g, '&lt;')
    .replace(/"/g, '&quot;');
}

export function svgImagePropsTransformer(
  rawSvg: string,
  {
    altText,
    svgInfo,
    displayMode,
    colorsMap,
    shapeStyle,
  }: SvgImageTransformedProps,
) {
  function withA11y(svgString: string) {
    const a11yAttrs = altText
      ? `role="img"`
      : `role="presentation" aria-hidden="true"`;
    let newSvgString = svgString.replace(svgTagMatcher, `$1 ${a11yAttrs}$2`);
    if (typeof altText === 'string') {
      newSvgString = newSvgString.replace(
        svgTagMatcher,
        `$1 aria-label="${escapeHtml(altText)}"$2`,
      );
    }
    return newSvgString;
  }

  function withScaledViewBox(svgString: string) {
    if (!svgString) {
      return svgString;
    }

    let svgTag = svgString.match(svgTagMatcher)?.[0];

    // no top svg tag so bail out
    if (!svgTag) {
      return svgString;
    }

    const { svgType, viewBox, bbox } = svgInfo;

    const preserveAspectRatio =
      displayMode === 'stretch' ? 'none' : 'xMidYMid meet';

    if (preserveAspectRatioMatcher.test(svgTag)) {
      svgString = svgString.replace(
        svgTag,
        svgTag.replace(
          preserveAspectRatioMatcher,
          `$1${preserveAspectRatio}$3`,
        ),
      );
    } else {
      svgString = svgString.replace(
        /<svg/,
        `<svg preserveAspectRatio="${preserveAspectRatio}"`,
      );
    }

    if (!viewBoxMatcher.test(svgTag)) {
      if (svgType === SVG_TYPES.UGC && viewBox) {
        svgString = svgString.replace(/<svg/, `<svg viewBox="${viewBox}"`);
      }
      if (svgType !== SVG_TYPES.UGC && bbox) {
        svgString = svgString.replace(/<svg/, `<svg viewBox="${bbox}"`);
      }
    }

    if (svgType !== SVG_TYPES.UGC && bbox) {
      svgTag = svgString.match(svgTagMatcher)![0];
      svgString = svgString.replace(
        svgTag,
        svgTag.replace(viewBoxMatcher, `$1${bbox}$3`),
      );
    }

    return svgString;
  }

  function withTintColors(svgString: string) {
    const DEFAULT_COLOR = '#242323';
    if (svgInfo.svgType !== SVG_TYPES.TINT) {
      return svgString;
    }
    const rawColor = shapeStyle?.['svg-color-1'] || DEFAULT_COLOR;
    const color = getFromColorMap(rawColor, colorsMap);

    const baseColor = Color(color);
    return svgString.replace(fillAttributeMatcher, (match, colorToTint) => {
      if (colorToTint === 'none' || colorToTint.startsWith('url(#')) {
        return match;
      }
      const colorObj = Color(colorToTint);

      // no change, return original svg color
      if (!isGreyscale(colorObj)) {
        return match;
      }

      const tint = 1 - (255 - colorObj.red()) / 255;
      const getCurrentTintColor = (chanel: number) => {
        return Math.floor(chanel + (255 - chanel) * tint);
      };
      const tintedColor = Color({
        r: getCurrentTintColor(baseColor.red()),
        g: getCurrentTintColor(baseColor.green()),
        b: getCurrentTintColor(baseColor.blue()),
      });
      // return tinted color
      return `fill="${tintedColor.hex().toString()}"`;
    });
  }

  const transformSvg = compose(withScaledViewBox, withTintColors, withA11y);

  return transformSvg(rawSvg);
}
